import { api } from './axios';
import { authService } from './auth';
import type { Demanda, CreateDemandaRequest, Observador } from '../models';
import { eventBus } from '../hooks/useEventBus';

class DemandaService {
  /**
   * Listar todas as demandas
   */
  async getAll(): Promise<Demanda[]> {
    // Garantir que Sanctum esteja inicializado
    await authService.ensureSanctumInitialized();
    
    const response = await api.get<Demanda[]>('/demandas');
    return response.data;
  }

  /**
   * Obter demanda por ID
   */
  async getById(id: number): Promise<Demanda> {
    await authService.ensureSanctumInitialized();
    
    const response = await api.get<Demanda>(`/demandas/${id}`);
    return response.data;
  }

  /**
   * Listar demandas em que o usuário é observador ativo
   */
  async getObservaveis(): Promise<Demanda[]> {
    await authService.ensureSanctumInitialized();

    const response = await api.get<Demanda[]>('/demandas/observaveis');
    return response.data;
  }

  /**
   * Criar nova demanda
   */
  async create(data: CreateDemandaRequest): Promise<Demanda> {
    // Garantir que Sanctum esteja inicializado antes de criar
    await authService.ensureSanctumInitialized();
    
    const formData = new FormData();
    
    // Adicionar campos principais
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'anexos') {
        // Anexos são tratados separadamente
        return;
      }
      if (key === 'processo_fk' && Array.isArray(value)) {
        // processo_fk como array
        value.forEach((processo, index) => {
          formData.append(`processo_fk[${index}]`, processo.toString());
        });
        return;
      }
      if (key === 'observadores' && Array.isArray(value)) {
        // Observadores como array
        value.forEach((observador, index) => {
          formData.append(`observadores[${index}]`, observador.toString());
        });
        return;
      }
      if (value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });

    // Adicionar anexos se existirem
    if (data.anexos && data.anexos.length > 0) {
      data.anexos.forEach((anexo: File, index: number) => {
        formData.append(`anexos[${index}]`, anexo);
      });
    }

    const response = await api.post<{ demanda?: Demanda; message?: string }>(
      '/demandas',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    const createdDemanda = response.data?.demanda;

    if (createdDemanda) {
      const sectorId = createdDemanda.setor?.id;
      eventBus.emit('demand-created', {
        demanda: createdDemanda,
        ...(sectorId ? { sectorId } : {}),
      });
      return createdDemanda;
    }

    // Caso a API não retorne o objeto esperado, devolve o payload bruto para evitar falso positivo de erro
    return (response.data as unknown) as Demanda;
  }

  /**
   * Atualizar demanda
   */
  async update(id: number, data: Partial<CreateDemandaRequest>): Promise<Demanda> {
    await authService.ensureSanctumInitialized();
    
    // Preparar dados para envio
    const updateData: any = {};
    
    // Processar cada campo
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'observadores' && Array.isArray(value)) {
        // Observadores como array
        updateData[key] = value;
        return;
      }
      if (key === 'demandado' && Array.isArray(value)) {
        // Demandados como array
        updateData[key] = value;
        return;
      }
      if (value !== null && value !== undefined) {
        updateData[key] = value;
      }
    });

    const response = await api.put<{ demanda: Demanda }>(`/demandas/${id}`, updateData);
    const updatedDemanda = response.data.demanda;
    
    // Emitir evento de demanda atualizada
    eventBus.emit('demand-updated', {
      demandId: id,
      changes: updateData,
      demanda: updatedDemanda
    });
    
    return updatedDemanda;
  }

  /**
   * Deletar demanda
   */
  async delete(id: number): Promise<{ message: string; edicaoId: number }> {
    await authService.ensureSanctumInitialized();
    
    const response = await api.delete<{ message: string; edicaoId: number }>(`/demandas/${id}`);
    return response.data;
  }

  /**
   * Puxar demanda para o usuário atual (atribuir para si)
   */
  async puxar(id: number): Promise<{ message: string; demanda: Demanda; edicaoId: number }> {
    await authService.ensureSanctumInitialized();
    
    const response = await api.post<{ message: string; demanda: Demanda; edicaoId: number }>(`/demandas/${id}/puxar`);
    return response.data;
  }

  /**
   * Solucionar demanda
   */
  async solucionar(id: number, comentario?: string, nota?: number): Promise<{ message: string; alterados: any; edicaoId: number }> {
    await authService.ensureSanctumInitialized();
    
    const response = await api.post<{ message: string; alterados: any; edicaoId: number }>(`/demandas/${id}/solucionar`, {
      comentario,
      nota
    });
    return response.data;
  }

  /**
   * Solucionar demanda por todos os responsáveis
   */
  async solucionarTudo(id: number, comentario: string, nota?: number): Promise<{ message: string; alterados: any; edicaoId: number }> {
    await authService.ensureSanctumInitialized();
    
    const response = await api.post<{ message: string; alterados: any; edicaoId: number }>(`/demandas/${id}/solucionartudo`, {
      comentario,
      nota
    });
    
    return response.data;
  }

  /**
   * Finalizar demanda (aceitar entrega)
   */
  async finalizar(id: number, comentario?: string, nota?: number): Promise<{ message: string; alterados: any; edicaoId: number }> {
    await authService.ensureSanctumInitialized();
    
    const response = await api.post<{ message: string; alterados: any; edicaoId: number }>(`/demandas/${id}/finalizar`, {
      comentario,
      nota
    });
    return response.data;
  }

  /**
   * Desfazer entrega da demanda
   */
  async desfazer(id: number, comentario?: string): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    const response = await api.post<{ message: string }>(`/demandas/${id}/desfazer`, {
      comentario
    });
    return response.data;
  }

  /**
   * Devolver demanda (recusar solução)
   */
  async devolver(id: number, comentario?: string): Promise<{ message: string; alterados: any; edicaoId: number }> {
    await authService.ensureSanctumInitialized();
    
    const response = await api.post<{ message: string; alterados: any; edicaoId: number }>(`/demandas/${id}/devolver`, {
      comentario
    });
    
    return response.data;
  }

  /**
   * Atribuir demanda para usuários
   */
  async atribuir(id: number, usuarios: number[], comentario?: string): Promise<{ message: string; demandaId: number; usuarios: number[] }> {
    await authService.ensureSanctumInitialized();
    
    const response = await api.post<{ message: string; demandaId: number; usuarios: number[] }>(`/demandas/${id}/atribuir`, {
      usuarios,
      comentario
    });
    
    // Emitir evento de demanda atribuída
    eventBus.emit('demand-assigned', {
      demandId: id,
      assignedTo: usuarios
    });
    
    return response.data;
  }

  /**
   * Remover atribuído da demanda (apenas gestor)
   */
  async removerAtribuido(id: number, userId: number): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    
    const response = await api.post<{ message: string }>(`/demandas/${id}/desatribuir/${userId}`);
    
    // Emitir evento de atribuição removida
    eventBus.emit('demand-assignment-removed', {
      demandId: id,
      userId
    });
    return response.data;
  }

  /**
   * Aceitar demanda (muda status da atribuição de RECEBIDA para EMPROGRESSO)
   */
  async aceitar(id: number, comentario?: string): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    const response = await api.post<{ message: string }>(`/demandas/${id}/aceitar`, {
      comentario
    });
    return response.data;
  }

  /**
   * Recusar demanda
   */
  async recusar(id: number, comentario?: string): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    const response = await api.post<{ message: string }>(`/demandas/${id}/recusar`, {
      comentario
    });
    return response.data;
  }

  /**
   * Marcar demanda como errada
   */
  async errada(id: number, comentario?: string): Promise<{ message: string; demandaId: number }> {
    await authService.ensureSanctumInitialized();
    const response = await api.post<{ message: string; demandaId: number }>(`/demandas/${id}/errada`, {
      comentario
    });
    return response.data;
  }

  /**
   * Reenviar demanda (atualiza campos e registra historico)
   */
  async remandar(
    id: number,
    data: {
      descricao?: string | undefined;
      sugestao_entrega?: string | undefined;
      template_respostas?: {
        template_id: string;
        template_versao: string;
        preenchido_em: string;
        respostas: Record<string, unknown>;
      } | undefined;
      comentario?: string | undefined;
    }
  ): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    const response = await api.post<{ message: string }>(`/demandas/${id}/remandar`, data);
    return response.data;
  }

  /**
   * Remover anexos de uma demanda
   */
  async removeranexos(id: number, anexoIds: number[], comentario?: string): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    const response = await api.post<{ message: string }>(`/demandas/${id}/removeranexos`, {
      anexos: anexoIds,
      comentario
    });
    return response.data;
  }

  /**
   * Fazer comentário na demanda
   */
  async comentar(id: number, comentario: string): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    const response = await api.post<{ message: string }>(`/demandas/${id}/comentar`, {
      comentario,
    });
    return response.data;
  }

  /**
   * Anexar arquivos à demanda (com comentário opcional)
   */
  async anexar(id: number, anexos: File[], comentario?: string): Promise<{ message: string; anexos: any[] }> {
    await authService.ensureSanctumInitialized();
    const formData = new FormData();
    // Adicionar comentário se existir
    if (comentario && comentario.trim()) {
      formData.append('comentario', comentario);
    }
    
    // Adicionar anexos
    if (anexos && anexos.length > 0) {
      anexos.forEach((file, index) => {
        formData.append(`anexos[${index}]`, file);
      });
    }
    
    const response = await api.post<{ message: string; anexos: any[] }>(`/demandas/${id}/anexar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Anexar arquivos à demanda com comentários individuais
   */
  async anexarComComentarios(
    id: number, 
    attachmentsData: Array<{ file: File; comment: string }>, 
    comentarioGeral?: string
  ): Promise<{ message: string; anexos: any[] }> {
    await authService.ensureSanctumInitialized();
    const formData = new FormData();
    
    // Adicionar comentário geral se existir
    if (comentarioGeral && comentarioGeral.trim()) {
      formData.append('comentario_geral', comentarioGeral);
    }
    
    // Adicionar anexos com comentários individuais
    if (attachmentsData && attachmentsData.length > 0) {
      attachmentsData.forEach((item, index) => {
        formData.append(`anexos[${index}]`, item.file);
        if (item.comment && item.comment.trim()) {
          formData.append(`comentarios[${index}]`, item.comment);
        }
      });
    }
    
    const response = await api.post<{ message: string; anexos: any[] }>(`/demandas/${id}/anexar-com-comentarios`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Adicionar observadores à demanda
   */
  async addObservadores(id: number, observadores: number[], comentario?: string): Promise<{ message: string; observadores: any[] }> {
    await authService.ensureSanctumInitialized();
    
    const data = {
      observadores,
      comentario: comentario || null,
    };
    
    const response = await api.post<{ message: string; observadores: any[] }>(`/demandas/${id}/addObservadores`, data);
    return response.data;
  }

  /**
   * Recusar convite para ser observador da demanda
   */
  async recusarObservador(id: number): Promise<{ message: string; observador: Observador | null }> {
    await authService.ensureSanctumInitialized();

    const response = await api.post<{ message: string; observador: Observador | null }>(
      `/demandas/${id}/recusaObservador`
    );

    eventBus.emit('cache-invalidated', {
      cacheKeys: [`demandas.${id}`, 'demandas.observaveis']
    });

    return response.data;
  }

  /**
   * Remover observador da demanda
   */
  async removeObservador(id: number, observadorId: number, comentario?: string): Promise<{ message: string; observador: any }> {
    await authService.ensureSanctumInitialized();
    
    const data = {
      observador_id: observadorId,
      comentario: comentario || null,
    };
    
    const response = await api.post<{ message: string; observador: any }>(`/demandas/${id}/removeObservador`, data);
    return response.data;
  }

}

export const demandaService = new DemandaService();